import React, { useEffect, useState, useCallback } from "react";
import GridTable from "cvmgmt-cube-grid-table";
import { Pagination } from "cprime-foundational-components";
import CprimeButton from "../Common/CprimeButton";
import PanelComponent from "../PanelComponent/PanelComponent";
import ManageCreateVersion, { VersionFormState } from "./ManageCreateVersion";
import { GridTableDataType } from "cvmgmt-txn-history-ui-lib/types/Packages/GridTable/types";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { triggerToast } from "../../utils/toast";
import {
  getCubeAuthVersionList,
  registerCubeAuthVersion,
} from "../../services/RequestAccess/RequestAccess";
import { formatFilterValue } from "../../utils/formatFilters";

interface CubeAuthVersionRow {
  version: string;
  release_date?: string;
  tag?: string;
  published_at?: string;
  published_by?: string;
  current_in_app?: string;
  action?: {
    label?: string;
    disabled?: boolean;
  };
}

interface ContextProps {
  appId: number;
  appName?: string;
  isAccessible?: boolean;
  isPageView?: boolean;
}

const GRID_COLUMNS: GridTableDataType["columns"] = [
  {
    id: "version",
    name: "Version",
    size: 250,
  },
  {
    id: "release_date",
    name: "Release Date",
  },
  {
    id: "tag",
    name: "Tag",
  },
  {
    id: "published_at",
    name: "Published At",
  },
  {
    id: "published_by",
    name: "Published By",
  },
  {
    id: "current_in_app",
    name: "Current In App",
  },
  {
    id: "action",
    name: "Action",
    type: "ACTION",
  },
];

interface CubeAuthVersionListProps extends ContextProps {
  isCreateVersion?: boolean;
  setIsCreateVersion?: (value: boolean) => void;
  canEditVersion?: boolean;
}

const CubeAuthVersionList: React.FC<CubeAuthVersionListProps> = ({
  appId,
  appName = "",
  isAccessible = true,
  isPageView = false,
  isCreateVersion,
  setIsCreateVersion,
  canEditVersion = true,
}) => {
  const [gridData, setGridData] = useState<GridTableDataType>({
    columns: GRID_COLUMNS,
    rows: [],
    totalCount: 0,
  });
  const [currentVersion, setCurrentVersion] = useState<string>("");
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [refresh, setRefresh] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    row: CubeAuthVersionRow | null;
  }>({ open: false, row: null });
  const [publishing, setPublishing] = useState(false);
  const [versionPanel, setVersionPanel] = useState<{
    open: boolean;
    isEdit: boolean;
    id?: number;
    baseVersion: string;
    form: VersionFormState;
    saving: boolean;
  }>({
    open: false,
    isEdit: false,
    id: undefined,
    baseVersion: "",
    form: { version: "", release_date: "", tag: "" },
    saving: false,
  });

  const getRowData = (row: any) => row?.row?.original ?? row?.original ?? row;

  const closeVersionPanel = () => {
    setIsCreateVersion?.(false);
    setVersionPanel((prev) => ({
      ...prev,
      open: false,
      saving: false,
      id: undefined,
    }));
  };

  const openEditPanel = (rowData: CubeAuthVersionRow & { id?: number }) => {
    setIsCreateVersion?.(false);
    setVersionPanel({
      open: true,
      isEdit: true,
      id: rowData?.id,
      baseVersion: rowData?.version || "",
      form: {
        version: rowData?.version || "",
        release_date:
          rowData?.release_date && rowData?.release_date !== "-"
            ? rowData.release_date
            : "",
        tag: rowData?.tag && rowData?.tag !== "-" ? rowData.tag : "",
      },
      saving: false,
    });
  };

  const buildGridRows = useCallback(
    (versions: CubeAuthVersionRow[]) =>
      versions.map((v) => ({
        ...v,
        release_date: v.release_date || "-",
        tag: v.tag || "-",
        published_at: v.published_at || "-",
        published_by: v.published_by || "-",
        current_in_app: v.current_in_app || "No",
        action: [
          {
            value: "Edit",
            disabled: !canEditVersion,
          },
          {
            value: v?.action?.label || "Publish",
            disabled:
              Boolean(v?.action?.disabled) ||
              !isAccessible ||
              String(v?.current_in_app).toLowerCase() === "yes",
          },
        ],
      })),
    [isAccessible, canEditVersion],
  );

  const fetchVersions = useCallback(async () => {
    setIsLoading(true);
    try {
      const response: any = await getCubeAuthVersionList(
        isPageView ? undefined : String(appId),
        {
          sortingFilter: {
            filter:
              filters?.length > 0
                ? (filters || []).map((item: any) =>
                    formatFilterValue(item?.value, item?.id),
                  )
                : [],
            sort: sorting.map((item: any) => ({
              columnName: item?.id,
              type: item?.desc ? "DESC" : "ASC",
            })),
            page: {
              limit: pagination.rowsPerPage,
              offset: pagination.page * pagination.rowsPerPage,
            },
          },
        },
      );

      const payloadData = response?.data?.data ?? response?.data ?? {};
      const rows = Array.isArray(payloadData?.rows) ? payloadData.rows : [];
      const columns = Array.isArray(payloadData?.columns)
        ? payloadData.columns
        : GRID_COLUMNS;

      const activeVersionRow = rows.find(
        (row: CubeAuthVersionRow) =>
          String(row?.current_in_app).toLowerCase() === "yes",
      );

      setCurrentVersion(activeVersionRow?.version || "");
      setGridData({
        columns: columns.map((column: any) => ({
          ...column,
          size: column?.size ?? 200,
          minSize: column?.minSize ?? 200,
        })),
        rows: buildGridRows(rows),
        totalCount: payloadData?.totalCount ?? rows.length,
      });
      setRefresh((prev) => prev + 1);
    } catch (error: any) {
      triggerToast(
        error?.message || "Unable to load Cube Auth versions",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  }, [appId, isPageView, filters, sorting, pagination, buildGridRows]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  useEffect(() => {
    if (isCreateVersion && canEditVersion) {
      setVersionPanel({
        open: true,
        isEdit: false,
        id: undefined,
        baseVersion: "",
        form: { version: "", release_date: "", tag: "" },
        saving: false,
      });
    }
  }, [isCreateVersion, canEditVersion]);

  useEffect(() => {
    setPagination((prev) => {
      if (prev.page === 0) {
        return prev;
      }

      return {
        ...prev,
        page: 0,
      };
    });
  }, [filters]);

  const handlePublishConfirm = async () => {
    if (!confirmDialog.row) return;
    setPublishing(true);
    try {
      await registerCubeAuthVersion(confirmDialog.row.version, appId);
      triggerToast(
        `Version ${confirmDialog.row.version} published successfully`,
        "success",
      );
      setConfirmDialog({ open: false, row: null });
      fetchVersions();
    } catch (error: any) {
      triggerToast(error?.message || "Publish failed", "error");
    } finally {
      setPublishing(false);
    }
  };

  const handleSaveVersion = async (payload: {
    isEdit: boolean;
    baseVersion: string;
    version: string;
    release_date: string;
    tag: string;
  }) => {
    const version = payload.version.trim();

    if (!version) {
      triggerToast("Version is required", "error");
      return;
    }

    setVersionPanel((prev) => ({ ...prev, saving: true }));
    try {
      // Build API payload based on whether it's edit or create
      const apiPayload = payload.isEdit
        ? {
            version,
            edit: true,
            id: versionPanel.id,
          }
        : {
            version,
            ...(isPageView ? {} : { appId }),
          };

      const cubeAuthRegisterResponse = await registerCubeAuthVersion(
        version,
        appId,
        apiPayload,
      );
      if (cubeAuthRegisterResponse?.status === "SUCCESS") {
        triggerToast(
          payload.isEdit
            ? `Version ${version} updated successfully`
            : `Version ${version} created successfully`,
          "success",
        );
      } else {
        throw new Error(
          cubeAuthRegisterResponse?.error ||
            cubeAuthRegisterResponse?.message ||
            "Failed to save version",
        );
      }

      closeVersionPanel();
      fetchVersions();
    } catch (error: any) {
      triggerToast(error?.message || "Failed to save version", "error");
    } finally {
      setVersionPanel((prev) => ({ ...prev, saving: false }));
    }
  };

  const showInlineCreateButton = !setIsCreateVersion;

  return (
    <>
      <GridTable
        data={gridData}
        actions={[
          {
            event: "save",
            component: (
              <div
                style={{
                  position: "absolute",
                  left: "204px",
                  top: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {currentVersion && (
                  <span
                    style={{
                      border: "1px solid black",
                      borderRadius: "6px",
                      padding: "4px 6px",
                      fontSize: "13px",
                    }}
                  >
                    Current version in app: <strong>{currentVersion}</strong>
                  </span>
                )}
              </div>
            ),
          },
        ]}
        style={{
          height: isPageView ? "calc(100vh - 28rem)" : "calc(100vh - 34.5rem)",
          maxWidth: "calc(100vw - 4rem)",
        }}
        refresh={refresh}
        customPayload={true}
        isCustomizeColumns={false}
        title="Cube Auth Version List"
        showHeader={true}
        filters={filters}
        sorting={sorting}
        setSorting={setSorting}
        setFilters={setFilters}
        pagination={pagination}
        loading={isLoading}
        isPagination={false}
        showLoader={false}
        cellActions={[
          {
            event: "Edit",
            callback: (_event: any, row: any) => {
              const rowData = getRowData(row);
              if (!canEditVersion) return;
              const editAction = rowData?.action?.find(
                (item: any) => item?.value === "Edit",
              );
              if (editAction?.disabled) return;
              openEditPanel(rowData);
            },
          },
          {
            event: "Publish",
            callback: (_event: any, row: any) => {
              const rowData = getRowData(row);
              const publishAction = rowData?.action?.find(
                (item: any) => item?.value === "Publish",
              );
              if (publishAction?.disabled) return;
              setConfirmDialog({
                open: true,
                row: {
                  version: rowData?.version,
                  release_date: rowData?.release_date,
                  tag: rowData?.tag,
                  published_at: rowData?.published_at,
                  published_by: rowData?.published_by,
                  current_in_app: rowData?.current_in_app,
                },
              });
            },
            icon: false,
          },
        ]}
      />

      <Pagination
        sx={{ width: "100%", minHeight: "4.54rem", alignItems: "center" }}
        {...{
          ...pagination,
          count: gridData?.totalCount ?? 0,
          rowsPerPageOptions: [25, 50, 100],
          onPageChange: (_e: any, number: number) =>
            setPagination({ ...pagination, page: number }),
          onRowsPerPageChange: (evt: any) =>
            setPagination({
              page: 0,
              rowsPerPage: Number(evt.target.value ?? 25),
            }),
        }}
      />

      {versionPanel.open && canEditVersion && (
        <PanelComponent
          open={versionPanel.open}
          title={versionPanel.isEdit ? "Edit Version" : "Create Version"}
          isEdit={versionPanel.isEdit}
          onClose={closeVersionPanel}
          tabValue={7}
          BodyComponent={
            <ManageCreateVersion
              isEdit={versionPanel.isEdit}
              baseVersion={versionPanel.baseVersion}
              formData={versionPanel.form}
              onClose={closeVersionPanel}
              onSave={handleSaveVersion}
            />
          }
        />
      )}

      {confirmDialog.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          role="button"
          tabIndex={0}
          aria-label="Close publish confirmation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !publishing) {
              setConfirmDialog({ open: false, row: null });
            }
          }}
          onKeyDown={(e) => {
            if (
              (e.key === "Enter" || e.key === " " || e.key === "Escape") &&
              !publishing
            ) {
              setConfirmDialog({ open: false, row: null });
            }
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "8px",
              padding: "24px",
              minWidth: "360px",
              maxWidth: "480px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            }}
          >
            <p style={{ margin: "0 0 8px", fontSize: "16px", fontWeight: 600 }}>
              Confirm Publish Version
            </p>
            <p style={{ margin: "0 0 20px", fontSize: "14px", color: "#444" }}>
              Are you sure you want to set Cube Auth version&nbsp;
              <strong>{confirmDialog.row?.version}</strong> for&nbsp;
              <strong>{appName || `App ${appId}`}</strong>? This will be marked
              as current in app.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <CprimeButton
                composition="outline"
                variant="tertiary"
                size="medium"
                onClick={() =>
                  !publishing && setConfirmDialog({ open: false, row: null })
                }
                disabled={publishing}
              >
                Cancel
              </CprimeButton>
              <CprimeButton
                composition="filled"
                variant="primary"
                size="medium"
                onClick={handlePublishConfirm}
                disabled={publishing}
              >
                {publishing ? "Updating..." : "Confirm"}
              </CprimeButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CubeAuthVersionList;
